import { Box, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import type { FC } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import pageUrls from 'constants/pageUrls';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';
import Modal from 'components/Modal';
import StatusMessageBox from 'components/StatusMessageBox';

import { useAppDispatch } from 'redux-store';
import deleteRfidThunk from 'redux-store/thunks/rfids/deleteRfidThunk';

interface Props extends DefaultPopupProps {
  rfidId: number | null;
}

const RemoveRfidModal: FC<Props> = ({ onClose, rfidId, open }) => {
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const { t } = useTranslation();

  const removeRfid = useCallback(async () => {
    if (!rfidId) return;

    try {
      setLoading(true);
      const result = await dispatch(deleteRfidThunk(rfidId)).unwrap();

      if (!result.success) {
        setError(t('common:pages.rfids.remove_rfid_error'));
      } else {
        setSuccess(true);
        setError(null);
        setTimeout(async () => {
          await router.push(pageUrls.rfids.all);
          onClose();
          setSuccess(false);
        }, 1000);
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, onClose, router, rfidId, t]);

  return (
    <Modal open={open} onClose={onClose} hideCloseButton={success}>
      {!success ? (
        <Box height="100%" sx={{ overflowY: 'auto' }}>
          <Box mb={4}>
            <Typography
              variant="h2"
              textAlign="center"
              mb={2}
              data-testid="remove-rfid-modal"
            >
              {t('common:pages.rfids.deleteRfid')}
            </Typography>
            <Typography variant="body1" textAlign="center">
              {t('common:pages.rfids.remove_rfid_description')}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" gap={1} width="100%">
            <Button
              onClick={onClose}
              color="neutral"
              fullWidth
              variant="flat"
              size="large"
              data-testid="cancel-button"
            >
              {t('common:cancel')}
            </Button>
            <Button
              disabled={isLoading}
              onClick={removeRfid}
              fullWidth
              size="large"
              variant="flat"
              color="error"
              data-testid="submit"
            >
              {t('common:pages.rfids.deleteRfidButton')}
            </Button>
          </Box>
          <ErrorText error={error} mt={2} />
        </Box>
      ) : (
        <StatusMessageBox
          type="success"
          message={t('common:pages.rfids.rfid_removed')}
        />
      )}
    </Modal>
  );
};

export default RemoveRfidModal;
